import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Container, TextField, Button, Box, Avatar, Typography } from '@mui/material';
import { CloudUpload, Facebook, Instagram, Twitter } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { translations } from '../../translations';
import { useNavigate } from 'react-router-dom';

const VisuallyHiddenInput = styled('input')`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const EditProfile = () => {
  const { user } = useAuth();
  const language = localStorage.getItem('language') || 'ku';
  const isRtl = language === 'ku';
  const t = isRtl ? translations.ku : translations.en;
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    location: user?.location || '',
    social_links: {
      facebook: user?.social_links?.find(link => link.platform === 'facebook')?.url || '',
      instagram: user?.social_links?.find(link => link.platform === 'instagram')?.url || '',
      twitter: user?.social_links?.find(link => link.platform === 'twitter')?.url || '',
    },
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState(user?.profile_image_url || '');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (['facebook', 'instagram', 'twitter'].includes(name)) {
      setFormData(prev => ({
        ...prev,
        social_links: {
          ...prev.social_links,
          [name]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'social_links') {
          Object.entries(value as Record<string, string>).forEach(([platform, url]) => {
            formDataToSend.append(`social_links[${platform}]`, url);
          });
        } else {
          formDataToSend.append(key, value as string);
        }
      });
      if (profileImage) {
        formDataToSend.append('profile_image_url', profileImage);
      }

      const response = await fetch(`/api/users?id=${user?.id}`, {
        method: 'PUT',
        body: formDataToSend,
      });

      if (!response.ok) throw new Error('Update failed');

      const result = await response.json();
      if (result.success) {
        setMessage(t.profileUpdateSuccess);
        navigate(`/users/${user?.id}`);
      } else {
        setMessage(t.profileUpdateError);
      }
    } catch (error) {
      setMessage(t.profileUpdateError);
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <div>{t.unauthorized}</div>;

  return (
    <Container maxWidth="sm" dir={isRtl ? 'rtl' : 'ltr'}>
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4 }}>
          <Avatar
            src={previewUrl}
            sx={{ width: 100, height: 100, mb: 2 }}
          />
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUpload />}
          >
            {t.uploadPhoto}
            <VisuallyHiddenInput
              type="file"
              accept="image/*"
              onChange={handleImageChange}
            />
          </Button>
        </Box>

        <TextField
          fullWidth
          margin="normal"
          label={t.name}
          name="name"
          value={formData.name}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.email}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.phone}
          name="phone"
          value={formData.phone}
          onChange={handleInputChange}
        />
        <TextField
          fullWidth
          margin="normal"
          label={t.location}
          name="location"
          value={formData.location}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Facebook"
          name="facebook"
          value={formData.social_links.facebook}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: <Facebook sx={{ mr: 1, color: '#1877F2' }} />
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Instagram"
          name="instagram"
          value={formData.social_links.instagram}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: <Instagram sx={{ mr: 1, color: '#E1306C' }} />
          }}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Twitter"
          name="twitter"
          value={formData.social_links.twitter}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: <Twitter sx={{ mr: 1, color: '#1DA1F2' }} />
          }}
        />

        {message && (
          <Typography 
            color={message === t.profileUpdateSuccess ? 'success.main' : 'error.main'}
            sx={{ mt: 2 }}
          >
            {message}
          </Typography>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? t.saving : t.saveChanges}
        </Button>
      </Box>
    </Container>
  );
}

export default EditProfile;